import { memo, useCallback, useEffect, useState } from "react";
import { TenantInfoCardProps } from "./types";
import { useTranslate } from "helpers";
import { useDispatch, useSelector } from "react-redux";
import { Tenant } from "services/api/tenants/types";
import { TextField } from "ui/components/TextField/TextField";
import { Button } from "ui/components/Button/Button";
import TenantEditCard from "./components/TenantEditCard/TenantEditCard";
import { fetchActiveUser, getActiveUser } from "services/api/users";
import { Loader } from "ui/components/Loader";
import { TableSearch } from "ui/components/Table/TableSearch";
import { TENANT_BANK_ACCOUNT_COLUMNS } from "./consts";
import OibTextField from "ui/components/TextField/OibTextField/OibTextField";
import { EmailTextField } from "ui/components/TextField/EmailTextField";
import { WebTextField } from "ui/components/TextField/WebTextField";
import { MobilePhoneTextField } from "ui/components/TextField/MobilePhoneTextField";
import { PhoneTextField } from "ui/components/TextField/PhoneTextField";


const TenantInfoCard: React.FC<TenantInfoCardProps> = () => {

  const { t } = useTranslate();
  const dispatch = useDispatch();

  const { user } = useSelector(getActiveUser);
  const [tenant, setTenant] = useState<Tenant>();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    if (user?.tenant)
      setTenant(user?.tenant);
  }, [user?.tenant]);

  const handleEditClick = useCallback(() => {
    setShowEditModal(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setShowEditModal(false);
    dispatch(fetchActiveUser() as any);
  }, [dispatch]);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
  }, []);

  if (!tenant)
    return <Loader isLoading={true}> </Loader>

  const isCompany = tenant.tenantType?.id === "C";
  const isPerson = tenant.tenantType?.id === "P";

  return (
    <>
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32">
        <div className="flex flex-col flex-1 max-w-[300px] ">
          <div>
            <p className="text-base font-bold text-slate-600 py-3">
              {t("admin.setting.tenant.title")}
            </p>
            <p className="text-sm">
              {t("admin.setting.tenant.description")}
            </p>

          </div>
        </div>
        <div className="flex justify-between items-center flex-1  px-3 my-3 ">
          <div className="flex flex-col min-w-[530px]">
            <div className="mb-3">
              <TextField
                type="text"
                name="tenantType"
                label={t("admin.setting.tenant.tenanttype")}
                placeholder={t("admin.setting.tenant.tenanttype.placeholder")}
                value={tenant.tenantType?.name}
                cssClassName="bg-white"
                disabled
              ></TextField>
            </div>
            {!isPerson && (
              <div className="mb-3">
                <TextField
                  type="text"
                  name="companyName"
                  label={t("admin.setting.tenant.companyname")}
                  placeholder={t("admin.setting.tenant.companyname.placeholder")}
                  value={tenant.companyName}
                  cssClassName="bg-white"
                  disabled
                ></TextField>
              </div>
            )}
            {!isCompany && (
              <div className="flex gap-4">
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="firstName"
                    label={t("admin.setting.tenant.firstname")}
                    placeholder={t("admin.setting.tenant.firstname.placeholder")}
                    value={tenant.firstName}
                    cssClassName="bg-white"
                    disabled
                  ></TextField>
                </div>
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="lastName"
                    label={t("admin.setting.tenant.lastname")}
                    placeholder={t("admin.setting.tenant.lastname.placeholder")}
                    value={tenant.lastName}
                    cssClassName="bg-white"
                    disabled
                  ></TextField>
                </div>
              </div>
            )}
            {!isPerson && (
              <div className="mb-3">
                <TextField
                  type="text"
                  name="headquartersAddress"
                  label={t("admin.setting.tenant.headquartersaddress")}
                  placeholder={t("admin.setting.tenant.headquartersaddress.placeholder")}
                  value={tenant.headquartersAddress}
                  cssClassName="bg-white"
                  disabled
                ></TextField>
              </div>
            )}
            <div className="flex gap-4 mb-3">
              <div className="flex-1">
                <PhoneTextField
                  type="text"
                  name="phoneNumber"
                  label={t("admin.setting.tenant.phonenumber")}
                  placeholder={t("admin.setting.tenant.phonenumber.placeholder")}
                  value={tenant.phoneNumber}
                  endIcon="/media/icons/phone.svg"
                  cssClassName="bg-white"
                  disabled
                ></PhoneTextField>
              </div>
              <div className="flex-1">
                <MobilePhoneTextField
                  type="text"
                  name="mobilePhoneNumber"
                  label={t("admin.setting.tenant.mobilephonenumber")}
                  placeholder={t("admin.setting.tenant.mobilephonenumber.placeholder")}
                  value={tenant.mobilePhoneNumber}
                  cssClassName="bg-white"
                  disabled
                ></MobilePhoneTextField>
              </div>
            </div>
            <div className="flex gap-4 mb-3">
              <div className="flex-1">
                <EmailTextField
                  type="email"
                  name="email"
                  label={t("admin.setting.tenant.email")}
                  placeholder=""
                  value={tenant.email}
                  autoComplete="email"
                  cssClassName="bg-white"
                  disabled
                ></EmailTextField>
              </div>
              <div className="flex-1">
                <WebTextField
                  type="url"
                  name="web"
                  label={t("admin.setting.tenant.web")}
                  placeholder=""
                  value={tenant.web}
                  autoComplete="url"
                  cssClassName="bg-white"
                  disabled
                />
              </div>
            </div>
            <div className="mb-10">
              <OibTextField
                type="text"
                name="personalIdentificationNumber"
                label={t("admin.setting.tenant.personalidentificationnumber")}
                placeholder=""
                value={tenant.personalIdentificationNumber}
                cssClassName="bg-white"
                disabled
              />
            </div>

            <div className="mb-3 border border-slate-300 rounded-sm p-3">
              <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                Bankovni računi
              </label>
              {tenant.bankAccounts && tenant.bankAccounts.length > 0 ? (
                <TableSearch
                  data={tenant.bankAccounts}
                  columns={TENANT_BANK_ACCOUNT_COLUMNS()}
                  selectableRows={false}
                  disablePagination={true}
                />
              ) : (
                <p className="text-sm text-slate-400">Nema dodanih bankovnih računa</p>
              )}
            </div>
            <div className="mb-3">
              <TextField
                type="text"
                name="timezone"
                label={t("admin.setting.tenant.timezone")}
                placeholder={t("admin.setting.tenant.timezone.placeholder")}
                value={tenant.timezone}
                cssClassName="bg-white"
                disabled
              ></TextField>
            </div>
            <div className="mb-3">
              {tenant.logoUpload?.url && (
                <div className="mb-3">
                  <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                    {t("admin.tenant.detail.logo")}
                  </label>

                  <img
                    src={tenant.logoUpload!.url}
                    alt="Tenant Logo"
                    className="mt-2 max-h-20 object-contain"
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <Button
              variant="outlined"
              children={t("admin.edit")}
              onClick={handleEditClick}
              startIcon="/media/icons/plus.svg"
            />
          </div>
        </div>
      </div>
      <TenantEditCard
        tenantId={tenant.id}
        show={showEditModal}
        onSuccess={handleEditSuccess}
        onClose={handleEditClose}
        
      />

    </>
  );
};

export default memo(TenantInfoCard);
